import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { AngularFireStorage } from '@angular/fire/storage';
import { PDFCheckBox, PDFDocument, PDFDropdown, PDFRadioGroup, PDFSignature, PDFTextField } from 'pdf-lib';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { AppState } from '../../../app.service';
import { AppVars } from '../../../app.vars';
import { AuthService } from '../../auth';
import { AppointmentDataService } from '../appointment-data/appointment-data.service';
import { PatientDataEvents } from '../patient-data/patient';
import { PatientDataService } from '../patient-data/patient-data.service';
import { PatientDocsService } from '../patient-docs/patient-docs.service';
import { PatientLoggerService } from '../patient-data/patient-note.service';
import { CaseDataComposerService } from '../case-data/case-data-composer.service';
import { DwcFormConfig } from './dwc-forms.model';

@Injectable({
  providedIn: 'root',
})
export class DwcFormsService {
  private templateCache = new Map<string, ArrayBuffer>();

  constructor(
    private db: AngularFireDatabase,
    private afStorage: AngularFireStorage,
    private _as: AppState,
    private caseDataComposer: CaseDataComposerService,
    private patientDocsService: PatientDocsService,
    private logger: PatientLoggerService
  ) {}

  private getOrgRootPath(): string {
    const orgCode = this._as.get(AppVars.ORG_CODE);
    return `provider-org-${orgCode}`;
  }

  /**
   * Retrieves active DWC form catalog from RTDB: /provider-org-{orgCode}/dwc-forms
   */
  public getDwcForms(): Observable<DwcFormConfig[]> {
    const catalogPath = `${this.getOrgRootPath()}/dwc-forms`;
    return this.db
      .list(catalogPath)
      .snapshotChanges()
      .pipe(
        map((snapshots) =>
          snapshots
            .map((snap) => {
              const val = (snap.payload.val() || {}) as any;
              return {
                id: snap.key,
                ...val,
              } as DwcFormConfig;
            })
            .filter((form) => form.active !== false && !!form.storagePath)
        )
      );
  }

  /**
   * Downloads a PDF template from Cloud Storage and caches it in-memory.
   * Path format: /provider-org-{orgCode}/orgdata/dwc-forms/{formCode}.pdf
   */
  public async getTemplateBuffer(storagePath: string): Promise<ArrayBuffer> {
    if (!storagePath) {
      throw new Error('Template storage path is required');
    }
    const cleanPath = storagePath.startsWith('/') ? storagePath.substring(1) : storagePath;

    if (this.templateCache.has(cleanPath)) {
      const cached = this.templateCache.get(cleanPath)!;
      return cached.slice(0);
    }

    try {
      const fileRef = this.afStorage.ref(cleanPath);
      const downloadUrl = await fileRef.getDownloadURL().toPromise();
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch template from storage: ${response.statusText}`);
      }
      const buffer = await response.arrayBuffer();
      this.templateCache.set(cleanPath, buffer);
      return buffer.slice(0);
    } catch (error) {
      console.error('[DwcFormsService] Error downloading template:', storagePath, error);
      throw error;
    }
  }

  /**
   * Aggregates active case data across patient demographics, employer/carrier,
   * appointments, facility, doctor, attorneys, clinical findings (SOAP), and work status
   * using the unified application-wide CaseDataComposerService.
   */
  public async collateCaseData(patientKey: string): Promise<any> {
    return this.caseDataComposer.getComposedCaseData({ patientKey });
  }

  /**
   * Instantiates an interactive PDFDocument and pre-populates matching form fields.
   * AcroForms remain unflattened so users can review and edit in-browser.
   */
  public async createPopulatedPdf(templateBuffer: ArrayBuffer, data: any, formTitle?: string): Promise<PDFDocument> {
    const pdfDoc = await PDFDocument.load(templateBuffer);
    if (formTitle) {
      pdfDoc.setTitle(formTitle);
    }
    pdfDoc.setAuthor(data.orgName || 'OrganizeMED');
    pdfDoc.setProducer('OrganizeMED by Pranava Inc');

    const form = pdfDoc.getForm();
    const fields = form.getFields();

    fields.forEach((field) => {
      const key = field.getName();
      try {
        if (field instanceof PDFRadioGroup) {
          if (data[key]) {
            form.getRadioGroup(key).select(data[key]);
          }
        } else if (field instanceof PDFTextField) {
          if (data[key] !== undefined && data[key] !== null) {
            form.getTextField(key).setText(`${data[key]}`);
          }
        } else if (field instanceof PDFCheckBox) {
          if (data[key] === true || data[key] === 'Yes' || data[key] === 'true') {
            form.getCheckBox(key).check();
          }
        } else if (field instanceof PDFDropdown) {
          if (data[key]) {
            form.getDropdown(key).select(data[key]);
          }
        } else if (field instanceof PDFSignature) {
          // Signature fields handled via standard signing workflows
        }
      } catch (error) {
        console.warn('[DwcFormsService] Skipped field during population:', key, error);
      }
    });

    return pdfDoc;
  }

  /**
   * Flattens interactive PDF fields, uploads the immutable PDF to Firebase Storage,
   * and creates a record in the patient's filestore RTDB under the "Reports" category.
   */
  public async finalizeAndSaveToMedicalRecords(
    pdfDoc: PDFDocument,
    formConfig: DwcFormConfig,
    patientKey: string,
    patient: any
  ): Promise<{ fileName: string; downloadUrl: string; docId: string }> {
    const orgCode = this._as.get(AppVars.ORG_CODE);
    const user = this._as.get(AppVars.USER_ACCOUNT) || {};

    // 1. Flatten AcroForm fields
    try {
      pdfDoc.getForm().flatten();
    } catch (e) {
      console.warn('[DwcFormsService] Note on flattening:', e);
    }
    const pdfBytes = await pdfDoc.save();

    // 2. Generate standardized immutable file name
    const rawCode = formConfig.code || formConfig.formCode || 'DWC-FORM';
    const safeCode = rawCode.replace(/[^a-zA-Z0-9-_]/g, '_');
    const safeLastName = (patient?.lastName || 'Patient').replace(/[^a-zA-Z0-9-_]/g, '_');
    const safeFirstName = (patient?.firstName || '').replace(/[^a-zA-Z0-9-_]/g, '_');
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const dateStamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const timeStamp = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const fileName = `${safeCode}_${safeLastName}${safeFirstName ? '_' + safeFirstName : ''}_${dateStamp}_${timeStamp}.pdf`;

    let pageCount: number | null = null;
    try {
      pageCount = pdfDoc.getPageCount();
    } catch (e) {
      console.warn('[DwcFormsService] Could not determine page count:', e);
    }

    // 3. Upload to Firebase Storage: provider-org-{orgCode}/filestore/{patientKey}/{fileName}
    const storageUploadPath = `provider-org-${orgCode}/filestore/${patientKey}/${fileName}`;
    const fileRef = this.afStorage.ref(storageUploadPath);

    const uploadTask = fileRef.put(pdfBytes, {
      contentType: 'application/pdf',
      customMetadata: {
        formCode: formConfig.code || '',
        patientKey: patientKey,
        uploadedBy: user.displayName || user.email || 'Staff',
      },
    });

    await uploadTask.snapshotChanges().toPromise();
    const downloadUrl = await fileRef.getDownloadURL().toPromise();

    // 4. Save file record into RTDB: /provider-org-{orgCode}/patients/{patientKey}/filestore
    const fileRecord = {
      name: fileName,
      docName: fileName,
      fileName: fileName,
      description: fileName,
      category: 'Reports',
      fileType: 'application/pdf',
      type: 'application/pdf',
      downloadURL: downloadUrl,
      downloadUrl: downloadUrl,
      size: pdfBytes.byteLength,
      pageCount: pageCount,
      uploadedBy: user.displayName || user.email || 'Staff',
      createdOn: new Date().toISOString(),
      updated: new Date().toISOString(),
      status: 'FINAL',
    };

    const pushResult = await this.patientDocsService.saveFileRecord(patientKey, fileRecord);
    const docId = pushResult?.key || '';

    // 5. Patient audit logging
    this.logger.patientLogger$.next({
      patientKey,
      event: PatientDataEvents.DWC_FORM_PRINTED,
      extra: ` - ${formConfig.code || formConfig.title}`,
    });

    return { fileName, downloadUrl, docId };
  }
}
